/**
 * Role-based access control.
 *
 * This file is the machine-readable form of docs/RBAC_MATRIX.md, and it is the
 * source the Firestore rules test suite asserts against. If the matrix document
 * and this file disagree, this file is wrong — the document is what the
 * Department signed off on.
 *
 * TWO RULES CUT ACROSS THE WHOLE MATRIX:
 *
 *   1. AGGREGATES CROSS JURISDICTION BOUNDARIES; IDENTITIES DO NOT.
 *      A Chief Engineer in Multan may see that Lahore Zone has 642 cases. They
 *      may not see the name or CNIC of anyone in a Lahore case.
 *
 *   2. `admin` IS A SYSTEM ADMINISTRATOR, NOT A SUPER-USER.
 *      It manages accounts, routing rules and master data, and may read the
 *      audit log. It may NOT read case personal data, and may NOT alter a
 *      decision or an amount. Separating these keeps the most powerful account
 *      in the system from also being the most rewarding one to compromise.
 */
import { ROLE_SCOPE } from './enums.js';
/** Fields that are personal data and must never leave the owning jurisdiction. */
export const PERSONAL_DATA_FIELDS = [
    'culprit.name',
    'culprit.nameUr',
    'culprit.fatherName',
    'culprit.fatherNameUr',
    'culprit.cnic',
    'culprit.phone',
    'culprit.address',
];
/* -------------------------------------------------------------------------- */
/* Scope containment                                                          */
/* -------------------------------------------------------------------------- */
/**
 * Does `claims` cover the jurisdiction of `record`?
 *
 * Each role is anchored at one level of the tree and sees everything at or below
 * it. A missing ID on the claims side at the required level means "no access" —
 * never "all access". Getting that default wrong is how privilege-escalation
 * bugs appear in systems like this, so it is asserted explicitly in the tests.
 */
export function coversJurisdiction(claims, record) {
    const level = ROLE_SCOPE[claims.role];
    switch (level) {
        case 'province':
            return true;
        case 'zone':
            return !!claims.zoneId && claims.zoneId === record.zoneId;
        case 'circle':
            return !!claims.circleId && claims.circleId === record.circleId;
        case 'division':
            return !!claims.divisionId && claims.divisionId === record.divisionId;
        case 'subdivision':
            return !!claims.subDivisionId && claims.subDivisionId === record.subDivisionId;
    }
}
const CAPABILITIES = {
    secretary: [
        // The Cell answers to the Secretary, so the Secretary can see everything
        // the Cell can see — including who reported. That is oversight of the
        // Cell itself: somebody has to be able to check that the people holding
        // every whistleblower's identity are handling them properly, and it
        // cannot be the Cell.
        'report.readReporter',
        'report.triage',
        'report.route',
        'report.inspect',
        'handover.read',
        'handover.acknowledge',
        'case.read',
        'case.readPersonalData',
        'audit.read',
        'stats.readProvince',
    ],
    chief_engineer: [
        // A zone accepts or refuses a ticket. NOT `report.readReporter`: a Chief
        // Engineer sees the ticket number, where it is, and what the Cell attested
        // the video showed — never who filed it.
        'report.route',
        'handover.read',
        'handover.acknowledge',
        'case.read',
        'case.readPersonalData',
        'appeal.file',
        'appeal.decide',
        'voucher.reconcile',
        'stats.readProvince',
    ],
    superintendent_engineer: [
        'handover.read',
        'handover.acknowledge',
        'case.read',
        'case.readPersonalData',
        'appeal.file',
        'appeal.decide',
        'voucher.verify',
        'voucher.reconcile',
        'stay.record',
        'stay.vacate',
    ],
    xen: [
        'handover.read',
        'handover.acknowledge',
        'case.read',
        'case.readPersonalData',
        'case.decide',
        'case.void',
        'appeal.file',
        'appeal.decide',
        'voucher.verify',
        'voucher.reconcile',
        'stay.record',
        'stay.vacate',
    ],
    sdo: [
        // The SDO investigates a ticket and closes it, including marking one
        // fabricated. They too never see the reporter.
        'report.inspect',
        'handover.read',
        'handover.acknowledge',
        'case.read',
        'case.readPersonalData',
        'case.decide',
        'wire.confirm',
        'voucher.verify',
        'appeal.file',
    ],
    sub_engineer: ['handover.read', 'case.read', 'wire.create', 'report.inspect'],
    zilladar: ['handover.read', 'case.read', 'case.readPersonalData'],
    patwari: ['handover.read', 'case.read', 'case.readPersonalData', 'case.create'],
    data_operator: [
        'handover.read',
        'case.read',
        'case.readPersonalData',
        'case.create',
        'voucher.create',
        // An operator may RECORD that a court granted a stay and that an appeal was
        // filed — both are clerical acts of entering what a paper order says. They
        // may not decide an appeal and may not vacate a stay, because those change
        // what the department may do to a person.
        'appeal.file',
        'stay.record',
    ],
    // Deliberately NOT case.readPersonalData, case.decide or case.void — see the
    // header note. An admin who can silently change a fine is an audit failure.
    admin: ['user.manage', 'masterData.manage', 'penaltySchedule.verify', 'audit.read'],
    /**
     * The Public Liaison Cell.
     *
     * The narrowest role in the system and the most sensitive. It holds the
     * identity of every citizen who reports water theft, and it holds NOTHING
     * else: no case access, no personal data on an accused, no statistics, no
     * audit log. It cannot decide anything, price anything or void anything.
     *
     * That is the trade the design rests on. Asking a citizen for their CNIC is
     * only defensible if the number of people who can see it is very small and
     * their access is good for nothing else — a compromised Cell account
     * exposes reporters and reaches no further into the department.
     */
    public_liaison: ['report.readReporter', 'report.triage'],
};
/**
 * Every capability the matrix actually grants to somebody, at runtime.
 *
 * Derived from the matrix rather than written beside it, so the two cannot
 * drift. It exists so a test can assert what a role holds as an EXACT SET
 * rather than by sampling — which matters for `public_liaison`, where the
 * whole argument for asking a citizen's CNIC is that the role can do nothing
 * else.
 */
export const ALL_CAPABILITIES = [
    ...new Set(Object.values(CAPABILITIES).flat()),
].sort();
export function can(role, capability) {
    return CAPABILITIES[role].includes(capability);
}
export function capabilitiesOf(role) {
    return CAPABILITIES[role];
}
/**
 * The complete check the UI and the edge functions both call: does this actor
 * hold the capability AND does their jurisdiction cover this record?
 *
 * Firestore rules re-implement this same logic in the rules language — they are
 * the real enforcement point, because a client-side check is only a courtesy.
 * The rules test suite asserts that both implementations agree, including on
 * the negative cases (an SDO in Multan must NOT read a Lahore case).
 */
export function authorise(claims, capability, record) {
    if (!can(claims.role, capability)) {
        return { allowed: false, reasonKey: 'rbac.capabilityNotHeld' };
    }
    if (record && !coversJurisdiction(claims, record)) {
        return { allowed: false, reasonKey: 'rbac.outsideJurisdiction' };
    }
    return { allowed: true };
}
/**
 * Conflict-of-interest / recusal control (gap D3).
 *
 * Nothing in the original blueprint stopped one officer from reporting a wire
 * and then adjudicating it, or from verifying the bank voucher for a fine they
 * themselves imposed. Both are enforced here, in the UI, and again in the
 * Firestore rules.
 */
export function checkRecusal(actorUid, originatorUid) {
    if (originatorUid && actorUid === originatorUid) {
        return { allowed: false, reasonKey: 'rbac.selfActionForbidden' };
    }
    return { allowed: true };
}
/**
 * Maker–checker on recovery vouchers: whoever entered a voucher may not be the
 * one who verifies it. This is the control that makes the stamped-voucher
 * requirement mean something.
 */
export function checkMakerChecker(verifierUid, enteredByUid) {
    if (verifierUid === enteredByUid) {
        return { allowed: false, reasonKey: 'rbac.makerCheckerViolation' };
    }
    return { allowed: true };
}
//# sourceMappingURL=rbac.js.map